import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Breadcrumbs,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  useTheme
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  Assignment,
  Group,
  Timeline,
  TrendingUp,
  Science,
  ExpandLess,
  OpenInNew,
  Insights,
  ViewList,
  ListAlt,
  Person,
  Category,
  DirectionsRun,
  Traffic,
  Label,
  CalendarToday,
  Info,
  Link as LinkIcon,
  DateRange
} from '@mui/icons-material';
import ApiService from '../services/apiService';
import EpicInsightsView from '../components/EpicInsights/EpicInsightsView';
import MetricCard from '../components/EpicInsights/MetricCard';
import TimelineView from '../components/EpicInsights/TimelineView';
import { TeamSummaryTable, type TeamSummaryData } from '../components/shared';
import { apiClient } from '../services/apiService';
import { getStatusHexColor } from '../utils/statusColors';
import { calculateProgressPercentage } from '../utils/progressCalculations';
import Footer from '../components/Footer';
import { useTheme as useAppTheme } from '../App';
import StandardAppBar from '../components/StandardAppBar';
import StandardBreadcrumbs from '../components/StandardBreadcrumbs';

// Helper function to extract text from Atlassian Document Format (ADF)
const extractTextFromADF = (adfContent: any): string => {
  if (typeof adfContent === 'string') {
    return adfContent;
  }

  if (!adfContent || typeof adfContent !== 'object') {
    return '';
  }

  const extractTextRecursive = (node: any): string => {
    let text = '';

    // Handle text nodes
    if (node.type === 'text' && node.text) {
      text += node.text;
    }

    // Handle hard breaks (line breaks within paragraphs)
    if (node.type === 'hardBreak') {
      text += '\n';
    }

    // Handle content array
    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((item: any) => {
        text += extractTextRecursive(item);
      });
    }

    return text;
  };

  let result = '';

  // Handle content array at root level
  if (adfContent.content && Array.isArray(adfContent.content)) {
    adfContent.content.forEach((item: any) => {
      result += extractTextRecursive(item);
      // Add line break after paragraphs
      if (item.type === 'paragraph') {
        result += '\n';
      }
    });
  } else {
    result = extractTextRecursive(adfContent);
  }

  return result.trim();
};

// Helper function to map team names using backend service
const mapTeamName = async (sprintName: string): Promise<string> => {
  if (!sprintName || sprintName === 'Unknown Team') {
    return 'Unknown Team';
  }

  try {
    const response = await apiClient.get(`/team-filters/debug-mapping?sprint=${encodeURIComponent(sprintName)}`);
    if (response.data.success && response.data.result) {
      return response.data.result.team;
    }
  } catch (error) {
    console.warn('Failed to map team name via backend:', error);
  }

  // Fallback to original sprint name
  return sprintName;
};

// Helper function to group epics by team with calculated metrics
interface TeamGroup {
  teamName: string;
  epics: Epic[];
  totalPoints: number;
  completedPoints: number;
  completionRate: number;
}

const groupEpicsByTeam = (epics: Epic[]): TeamGroup[] => {
  // Group epics by team
  const grouped = epics.reduce((acc, epic) => {
    const teamName = epic.team || 'Unknown';
    if (!acc[teamName]) {
      acc[teamName] = [];
    }
    acc[teamName].push(epic);
    return acc;
  }, {} as Record<string, Epic[]>);

  // Sort teams alphabetically and calculate metrics
  return Object.keys(grouped)
    .sort((a, b) => {
      // Put "Unknown" at the end
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return a.localeCompare(b);
    })
    .map(teamName => {
      const teamEpics = grouped[teamName];
      // Calculate team totals
      const totalPoints = teamEpics.reduce((sum, epic) => sum + (epic.storyPoints || 0), 0);
      const completedPoints = teamEpics.reduce((sum, epic) => {
        const completed = epic.insights?.completedStoryPoints ||
          (epic.status?.statusCategory?.key?.toLowerCase() === 'done' ? (epic.storyPoints || 0) : 0);
        return sum + completed;
      }, 0);
      const completionRate = calculateProgressPercentage(completedPoints, totalPoints, 1);

      return {
        teamName,
        epics: teamEpics,
        totalPoints,
        completedPoints,
        completionRate
      };
    });
};

// Mock data indicator component
const MockDataIndicator: React.FC<{ tooltip?: string }> = ({ tooltip = "This is mock data for demonstration purposes" }) => (
  <Tooltip title={tooltip} arrow>
    <Chip
      icon={<Science />}
      label="MOCK"
      size="small"
      color="warning"
      variant="outlined"
      sx={{
        ml: 1,
        fontSize: '0.7rem',
        height: '20px',
        '& .MuiChip-icon': {
          fontSize: '0.8rem'
        }
      }}
    />
  </Tooltip>
);

interface Story {
  key: string;
  summary: string;
  storyPoints: number;
  status: {
    name: string;
    statusCategory: {
      name: string;
    };
  };
  assignee: {
    displayName: string;
    emailAddress: string;
  } | null;
  team: string;
  epic: string;
}

interface Epic {
  key: string;
  summary: string;
  status: {
    name: string;
    statusCategory: {
      name: string;
    };
  };
  storyPoints: number;
  stories: Story[];
  team: string;
  insights?: {
    totalStoryPoints: number;
    completedStoryPoints: number;
    progressPercentage: number;
  };
}

interface InitiativeDetail {
  key: string;
  summary: string;
  description: string;
  status: {
    name: string;
    statusCategory: {
      name: string;
    };
  };
  assignee: {
    displayName: string;
    emailAddress: string;
  } | null;
  labels: string[];
  components: Array<{ name: string }>;
  customFields: {
    theme: string;
    sprintField: string;
    customStatus?: string;
    trafficLight?: string;
    startedAtSprint?: string;
    endsAtSprint?: string;
    startDate?: string;
    endDate?: string;
    hasStartDate?: boolean;
    hasEndDate?: boolean;
    hasCompleteTimeline?: boolean;
  };
  created: string;
  updated: string;
  totalStoryPoints: number;
  completedStoryPoints: number;
  epics: Epic[];
}

const InitiativeDetails: React.FC = () => {
  const { initiativeKey } = useParams<{ initiativeKey: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { darkMode, toggleDarkMode } = useAppTheme();
  const [initiative, setInitiative] = useState<InitiativeDetail | null>(null);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [epicInsights, setEpicInsights] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [epicsLoading, setEpicsLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [epicsError, setEpicsError] = useState<string | null>(null);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<'basic' | 'insights'>('basic');

  const fetchEpicInsights = async () => {
    if (!initiativeKey) return;

    try {
      setInsightsLoading(true);
      setInsightsError(null);
      const response = await ApiService.getInitiativeEpicInsights(initiativeKey);

      setEpicInsights(response.data);
    } catch (err: any) {
      setInsightsError(err.message || 'Failed to fetch epic insights');
      console.error('Error fetching epic insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitiativeDetails = async () => {
      if (!initiativeKey) return;

      try {
        setLoading(true);
        const response = await ApiService.getInitiativeDetails(initiativeKey);
        setInitiative(response.data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch initiative details');
      } finally {
        setLoading(false);
      }
    };

    const fetchInitiativeEpics = async () => {
      if (!initiativeKey) return;

      try {
        setEpicsLoading(true);
        const response = await ApiService.getInitiativeEpics(initiativeKey, 50, 0, true);

        // Transform the API response to match the expected Epic interface

        // PERFORMANCE OPTIMIZATION: Batch team mapping to avoid N+1 API calls
        // Collect all unique team sprints first
        const uniqueTeamSprints = new Set<string>();
        response.data.forEach((epic: any) => {
          const rawTeamSprint = epic.customFields?.teamSprint;
          if (rawTeamSprint && rawTeamSprint !== 'Unknown Team') {
            uniqueTeamSprints.add(rawTeamSprint);
          }
        });

        // Create a cache for team mappings to avoid duplicate API calls
        const teamMappingCache = new Map<string, string>();

        // Map all unique team sprints in parallel (much faster than sequential)
        await Promise.all(
          Array.from(uniqueTeamSprints).map(async (teamSprint) => {
            const mappedName = await mapTeamName(teamSprint);
            teamMappingCache.set(teamSprint, mappedName);
          })
        );

        // Transform epics using the cached team mappings (no API calls here)
        const transformedEpics: Epic[] = response.data.map((epic: any) => {
          const rawTeamSprint = epic.customFields?.teamSprint || 'Unknown Team';
          const mappedTeamName = teamMappingCache.get(rawTeamSprint) || rawTeamSprint;

          return {
            key: epic.key,
            summary: epic.summary,
            status: epic.status,
            storyPoints: epic.customFields?.storyPoints || 0,
            team: mappedTeamName,
            stories: [], // For now, we'll show epics without drilling down to stories
            insights: epic.insights // Preserve insights data for completion tracking
          };
        });

        setEpics(transformedEpics);
      } catch (err: any) {
        setEpicsError(err.message || 'Failed to fetch initiative epics');
      } finally {
        setEpicsLoading(false);
      }
    };

    fetchInitiativeDetails();
    fetchInitiativeEpics();
  }, [initiativeKey]);

  // Fetch insights when switching to team view
  useEffect(() => {
    if (viewMode === 'insights' && !epicInsights && !insightsLoading) {
      fetchEpicInsights();
    }
  }, [viewMode, epicInsights, insightsLoading, fetchEpicInsights]);

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: 'basic' | 'insights'
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };





  const getTeamStoryPoints = (epics: Epic[]) => {
    const teamPoints: { [team: string]: { total: number; completed: number } } = {};

    epics.forEach(epic => {
      // Team mapping is now applied during data transformation
      const teamName = epic.team;

      if (!teamPoints[teamName]) {
        teamPoints[teamName] = { total: 0, completed: 0 };
      }
      teamPoints[teamName].total += epic.storyPoints || 0;
      // Use epic insights data for more accurate completion tracking
      if (epic.insights && epic.insights.completedStoryPoints !== undefined) {
        teamPoints[teamName].completed += epic.insights.completedStoryPoints;
      } else {
        // Fallback: check epic status (less accurate)
        const statusCategoryKey = epic.status.statusCategory.key?.toLowerCase();
        if (statusCategoryKey === 'done') {
          teamPoints[teamName].completed += epic.storyPoints || 0;
        }
      }
    });

    return teamPoints;
  };

  // Calculate totals from real epics data
  const totalStoryPoints = epics.reduce((sum, epic) => sum + (epic.storyPoints || 0), 0);
  // Calculate completed story points based on epic insights data (child story completion)
  const completedStoryPoints = epics.reduce((sum, epic) => {
    // Use epic insights data if available, otherwise fall back to epic status
    if (epic.insights && epic.insights.completedStoryPoints !== undefined) {
      return sum + epic.insights.completedStoryPoints;
    }
    // Fallback: check epic status (less accurate)
    const statusCategoryKey = epic.status.statusCategory.key?.toLowerCase();
    if (statusCategoryKey === 'done') {
      return sum + (epic.storyPoints || 0);
    }
    return sum;
  }, 0);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, pb: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, pb: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!initiative) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, pb: 8 }}>
        <Alert severity="warning">Initiative not found</Alert>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const teamStoryPoints = getTeamStoryPoints(epics);
  const progressPercentage = calculateProgressPercentage(completedStoryPoints, totalStoryPoints);

  // Transform team story points data for the TeamSummaryTable component
  const teamSummaryData: TeamSummaryData[] = Object.entries(teamStoryPoints).map(([name, points]) => ({
    name,
    total: points.total,
    completed: points.completed,
    progress: calculateProgressPercentage(points.completed, points.total)
  }));

  return (
    <>
      <StandardAppBar
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onRefresh={handleRefresh}
        isLoading={loading}
      />
      <Container maxWidth="lg" sx={{ mt: 2, mb: 2, pb: 8 }}> {/* Added bottom padding for fixed footer */}
      {/* Header */}
      <Box sx={{ mb: 2 }}> {/* Reduced from 4 to 2 */}
        {/* Navigation */}
        <StandardBreadcrumbs currentPage="Initiative Details" />

        {/* Main Header Section with Clear Hierarchy */}
        <Box sx={{ mb: 2 }}> {/* Reduced from 4 to 2 */}
          {/* Initiative Name */}
          <Typography variant="h4" component="h1" sx={{ mb: 1.5, fontWeight: 'bold', lineHeight: 1.2 }}> {/* Reduced margin */}
            {initiative.summary}
          </Typography>

          {/* New Header Row with Initiative Number, Status, and Timeline Dates */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexWrap: 'wrap',
            mt: 1
          }}>
            {/* Initiative Number with Link */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LinkIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              <Tooltip title="Open in JIRA">
                <Link
                  href={`${import.meta.env.VITE_JIRA_BASE_URL || 'https://redislabs.atlassian.net'}/browse/${initiative.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      color: 'primary.dark',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {initiative.key}
                </Link>
              </Tooltip>
            </Box>

            {/* Status Indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Chip
                label={initiative.status.name}
                size="small"
                sx={{
                  backgroundColor: getStatusHexColor(initiative.status.name, isDarkMode),
                  color: 'white',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: getStatusHexColor(initiative.status.name, isDarkMode)
                  }
                }}
              />
            </Box>

            {/* Timeline Dates */}
            {(initiative.customFields.startDate || initiative.customFields.endDate) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <DateRange sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {initiative.customFields.startDate && initiative.customFields.endDate ? (
                    `${new Date(initiative.customFields.startDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })} - ${new Date(initiative.customFields.endDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}`
                  ) : initiative.customFields.startDate ? (
                    `Start: ${new Date(initiative.customFields.startDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}`
                  ) : initiative.customFields.endDate ? (
                    `End: ${new Date(initiative.customFields.endDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}`
                  ) : null}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Initiative Description with expand/collapse */}
        {initiative.description && (
          <Box sx={{ mb: 1.5 }}> {/* Reduced from 3 to 1.5 */}
            <Typography
              variant="body1"
              sx={{
                mb: 0.5, // Reduced from 1 to 0.5
                display: '-webkit-box',
                WebkitLineClamp: isDescriptionExpanded ? 'none' : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.5, // Reduced from 1.6 to 1.5
                whiteSpace: 'pre-wrap' // Preserve line breaks and wrap text
              }}
            >
              {extractTextFromADF(initiative.description) || 'Description available in Jira'}
            </Typography>
            <Button
              size="small"
              startIcon={isDescriptionExpanded ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem', // Smaller font
                color: 'primary.main',
                py: 0.25, // Reduced vertical padding
                px: 0.5, // Reduced horizontal padding
                minHeight: 'auto' // Remove minimum height
              }}
            >
              {isDescriptionExpanded ? 'Show less' : 'Show more'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Data Source Notice */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Science />
          <Typography variant="body2">
            <strong>Data Source:</strong> All data including initiative details, epics, story points, and progress metrics are sourced from real JIRA data.
          </Typography>
        </Box>
      </Alert>

      {/* Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}> {/* Reduced spacing and margin */}
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Story Points"
            value={`${completedStoryPoints}/${totalStoryPoints}`}
            subtitle="Completed vs Total"
            icon={<Timeline />}
            color="primary"
            size="medium"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <MetricCard
            title="Progress"
            value={`${progressPercentage.toFixed(1)}%`}
            subtitle="Overall completion"
            icon={<TrendingUp />}
            color="warning"
            size="medium"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <MetricCard
            title="Child Epics"
            value={epics.length}
            subtitle="Total epics"
            icon={<ListAlt />}
            color="secondary"
            size="medium"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <MetricCard
            title="Teams"
            value={Object.keys(teamStoryPoints).length}
            subtitle="Active teams"
            icon={<Group />}
            color="success"
            size="medium"
          />
        </Grid>
      </Grid>

      {/* Initiative Details */}
      <Card sx={{ mb: 2 }}> {/* Reduced margin */}
        <CardContent sx={{ py: 2 }}> {/* Reduced padding */}
          <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>Initiative Information</Typography> {/* Made bold */}
          <Grid container spacing={1.5}> {/* Optimized spacing */}
            {/* Column 1: Basic Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Person sx={{ color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Assignee
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {initiative.assignee?.displayName || 'Unassigned'}
                </Typography>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Category sx={{ color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Theme
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {initiative.customFields.theme || 'No Theme'}
                </Typography>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <CalendarToday sx={{ color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Created
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {new Date(initiative.created).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>

            {/* Column 2: Sprint & Labels */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <DirectionsRun sx={{ color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Sprint
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {initiative.customFields.sprintField || 'No Sprint'}
                </Typography>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Traffic sx={{ color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Traffic Light
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {initiative.customFields.trafficLight || 'Not Set'}
                </Typography>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Label sx={{ color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Labels
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {initiative.labels && initiative.labels.length > 0 ? (
                    initiative.labels.map((label, index) => (
                      <Chip
                        key={index}
                        label={label}
                        size="small"
                        variant="outlined"
                        color="default"
                        sx={{ fontSize: '0.75rem', height: 20 }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No labels</Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Column 3: Status (Full width for long text) */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Info sx={{ color: 'primary.main', fontSize: 16 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Status
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-line',
                  lineHeight: 1.4,
                  wordBreak: 'break-word'
                }}
              >
                {initiative.customFields.customStatus || 'Not Set'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Team Summary */}
      <Card sx={{ mb: 2 }}> {/* Reduced margin */}
        <CardContent sx={{ py: 2 }}> {/* Reduced padding */}
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5 }}> {/* Reduced margin */}
            Team Story Points Summary
          </Typography>
          <TeamSummaryTable
            teamData={teamSummaryData}
            loading={epicsLoading}
            showProgress={true}
            variant="outlined"
            emptyMessage="No team data available"
          />
        </CardContent>
      </Card>



      {/* Epics and Stories */}
      <Card>
        <CardContent sx={{ py: 2 }}> {/* Reduced padding */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}> {/* Reduced margin */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {(epicsLoading || insightsLoading) && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="basic">
                <ViewList sx={{ mr: 1 }} />
                Basic View
              </ToggleButton>
              <ToggleButton value="insights">
                <Insights sx={{ mr: 1 }} />
                Team View
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Error Messages */}
          {epicsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {epicsError}
            </Alert>
          )}
          {insightsError && viewMode === 'insights' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {insightsError}
            </Alert>
          )}

          {/* No Epics Message */}
          {epics.length === 0 && !epicsLoading && !epicsError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No child epics found for this initiative.
            </Alert>
          )}

          {/* Basic View - Epic Timeline */}
          {viewMode === 'basic' && epics.length > 0 && (
            <Box>
              <Box sx={{ mb: 1.5 }}> {/* Reduced margin */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}> {/* Reduced margin */}
                  <ListAlt sx={{ mr: 0.75, color: 'primary.main', fontSize: 20 }} /> {/* Smaller icon and margin */}
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Child Epics
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}> {/* Smaller font */}
                  Timeline visualization showing epic progress, story points, and delivery status
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}> {/* Reduced margin */}
                Data sources: Start/End dates from JIRA custom fields (customfield_10534/10535),
                story points from epic aggregation, progress calculated from completed vs total story points.
              </Typography>

              {/* Group epics by team */}
              {(() => {
                const teamGroups = groupEpicsByTeam(epics);

                return teamGroups.map((group, groupIndex) => (
                  <Box key={group.teamName} sx={{ mb: groupIndex < teamGroups.length - 1 ? 3 : 0 }}>
                    {/* Team Header - Matching Team View Style */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : '#f1f5f9',
                      color: (theme) => theme.palette.mode === 'dark' ? 'grey.100' : '#334155',
                      borderRadius: 2,
                      border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      minHeight: 56
                    }}>
                      <Group sx={{ mr: 1, fontSize: 18 }} />
                      <Typography variant="h6" sx={{
                        fontWeight: 600,
                        flex: 1,
                        fontSize: '1rem',
                        letterSpacing: '-0.01em'
                      }}>
                        {group.teamName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{
                            opacity: 0.85,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mb: 0.25
                          }}>
                            Epics
                          </Typography>
                          <Typography variant="h6" sx={{
                            fontWeight: 600,
                            fontSize: '1rem'
                          }}>
                            {group.epics.length}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{
                            opacity: 0.85,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mb: 0.25
                          }}>
                            Total Points
                          </Typography>
                          <Typography variant="h6" sx={{
                            fontWeight: 600,
                            fontSize: '1rem'
                          }}>
                            {group.totalPoints}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{
                            opacity: 0.85,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mb: 0.25
                          }}>
                            Completion
                          </Typography>
                          <Typography variant="h6" sx={{
                            fontWeight: 600,
                            fontSize: '1rem'
                          }}>
                            {group.completionRate}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Epics for this team */}
                    <TimelineView
                      items={group.epics.map(epic => {
                        // Calculate progress from insights data or epic status
                        const completedSP = epic.insights?.completedStoryPoints ||
                          (epic.status?.statusCategory?.key?.toLowerCase() === 'done' ? (epic.storyPoints || 0) : 0);
                        const totalSP = epic.storyPoints || 0;
                        const progress = calculateProgressPercentage(completedSP, totalSP, 1);

                        // Determine risk level based on story points and status
                        let riskLevel: 'low' | 'medium' | 'high' = 'low';
                        if (totalSP > 50) riskLevel = 'high';
                        else if (totalSP > 20) riskLevel = 'medium';

                        return {
                          key: epic.key,
                          summary: epic.summary,
                          startDate: epic.customFields?.startDate,
                          endDate: epic.customFields?.endDate,
                          status: epic.status?.name || 'Unknown',
                          progress,
                          riskLevel,
                          storyPoints: {
                            total: totalSP,
                            completed: completedSP
                          },
                          team: epic.team || 'Unknown',
                          url: `https://redislabs.atlassian.net/browse/${epic.key}`
                        };
                      })}
                      title=""
                    />
                  </Box>
                ));
              })()}
            </Box>
          )}

          {/* Team View */}
          {viewMode === 'insights' && (
            <Box>
              {insightsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!insightsLoading && epicInsights && (
                <EpicInsightsView
                  epics={epicInsights.epics || []}
                  aggregatedInsights={epicInsights.aggregatedInsights || {}}
                  loading={insightsLoading}
                />
              )}

              {!insightsLoading && !epicInsights && !insightsError && (
                <Alert severity="info">
                  Click "Team View" to load team-focused insights.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
    <Footer />
    </>
  );
};

export default InitiativeDetails;
